// Token-driven CSS injected once at first render. Every visible surface
// reads from CSS variables computed by tokens.ts → applyTokensToRoot.
// Tenants pass `theme` config; this stylesheet paints accordingly. Both
// desktop modal (.web_components_modal_*) and mobile drawer (.ant-drawer-*)
// are covered. The global Oops popup (.overall_situation_modal_tips) is
// also auto-themed.
//
// All rules are scoped to widget-managed surfaces (.peblla-login-root /
// .overall_situation_modal_tips). Nothing leaks to the host page or the
// main site. !important is used because the project's SCSS uses 3- to
// 6-class deep selectors with !important; without it our token rules lose
// the specificity war.

const CSS = `
/* ============================================================
   1. Modal shell — login drawer + complete drawer (both tagged
      with .peblla-login-root) + global Oops popup
   ============================================================ */
.peblla-login-root .web_components_modal_content,
.peblla-login-root .ant-drawer-content,
.overall_situation_modal_tips .web_components_modal_content {
  background: var(--peblla-bg, #fff) !important;
  color: var(--peblla-text, #1f1f1f) !important;
  border: 1px solid var(--peblla-border, transparent) !important;
  border-radius: var(--peblla-radius, 8px) !important;
  box-shadow: var(--peblla-shadow, 0 8px 32px rgba(0, 0, 0, 0.08));
  font-family: var(--peblla-font-family, inherit);
}
.peblla-login-root .web_components_modal_content {
  width: var(--peblla-modal-width, 500px) !important;
  padding: var(--peblla-modal-padding, 20px 24px) !important;
}
.peblla-login-root .ant-drawer-content,
.peblla-login-root .ant-drawer-body,
.peblla-login-root .ant-drawer-wrapper-body {
  background: var(--peblla-bg, #fff) !important;
  color: var(--peblla-text, #1f1f1f) !important;
}
.peblla-login-root .ant-drawer-body {
  padding: var(--peblla-modal-padding, 20px 24px) !important;
}
.peblla-login-root .ant-drawer-content-wrapper {
  height: var(--peblla-drawer-height, 70%) !important;
}
.peblla-login-root.web_components_modal_root,
.peblla-login-root.ant-drawer {
  font-family: var(--peblla-font-family, inherit);
}

/* ============================================================
   2. Header / Title
   ============================================================ */
.peblla-login-root .web_components_modal_header,
.peblla-login-root .ant-drawer-header,
.overall_situation_modal_tips .web_components_modal_header {
  border-bottom-color: var(--peblla-border, rgba(0, 0, 0, 0.06)) !important;
  background: transparent !important;
}
/* complete 抽屉的长 title 会被项目原 SCSS 锁死的 line-height:44px 卡到只显示一行被裁切。
   仅在 .peblla-complete-drawer 上放宽，login 抽屉保持紧凑原生头不动 */
.peblla-complete-drawer .ant-drawer-header {
  height: auto !important;
  min-height: 44px;
  line-height: normal !important;
}
.peblla-complete-drawer .ant-drawer-title {
  line-height: 1.3 !important;
  white-space: normal !important;
  word-break: normal;
}
.peblla-login-root .web_components_modal_header,
.peblla-login-root .ant-drawer-title {
  color: var(--peblla-title-color, var(--peblla-text, #1f1f1f)) !important;
  font-family: var(--peblla-font-family-title, var(--peblla-font-family, inherit)) !important;
  font-size: var(--peblla-font-size-title, 18px) !important;
  font-weight: var(--peblla-font-weight-title, 600) !important;
  letter-spacing: var(--peblla-letter-spacing-title, normal) !important;
  text-transform: var(--peblla-text-transform-title, none) !important;
}
.overall_situation_modal_tips .web_components_modal_header {
  color: var(--peblla-text, #1f1f1f) !important;
  font-family: var(--peblla-font-family-title, var(--peblla-font-family, inherit)) !important;
}

/* Complete 弹窗的标题/副标题渲染在 body 内（不在 antd header 里），
   通过 data-peblla-part 标记，跟 login 的 ant-drawer-title 用相同的字号/字重 */
.peblla-login-root [data-peblla-part="complete-title"] {
  color: var(--peblla-title-color, var(--peblla-text, #1f1f1f)) !important;
  font-family: var(--peblla-font-family-title, var(--peblla-font-family, inherit)) !important;
  font-size: var(--peblla-font-size-title, 18px) !important;
  font-weight: var(--peblla-font-weight-title, 600) !important;
  letter-spacing: var(--peblla-letter-spacing-title, normal) !important;
  text-transform: var(--peblla-text-transform-title, none) !important;
  margin-bottom: 4px;
}
.peblla-login-root [data-peblla-part="complete-subtitle"] {
  color: var(--peblla-text-muted, rgba(0, 0, 0, 0.5)) !important;
  font-family: var(--peblla-font-family, inherit) !important;
  /* 项目 complete header_instruction 自然默认 12px，保持一致 */
  font-size: var(--peblla-font-size-agreement, 12px) !important;
  line-height: 16px;
  margin-bottom: 24px;
}
.peblla-login-root [data-peblla-part="complete-name-row"] {
  display: flex;
  gap: 12px;
}
.peblla-login-root [data-peblla-part="complete-name-row"] > * {
  flex: 1;
  min-width: 0;
}

/* ============================================================
   3. Close icon — render a clean SVG ✕ via mask-image
   ============================================================ */
.peblla-login-root .iconfont.icon-close,
.overall_situation_modal_tips .iconfont.icon-close {
  display: inline-block !important;
  width: 16px !important;
  height: 16px !important;
  font-style: normal !important;
  vertical-align: middle;
}
.peblla-login-root .iconfont.icon-close::before,
.overall_situation_modal_tips .iconfont.icon-close::before {
  content: '' !important;
  display: block;
  width: 100%;
  height: 100%;
  background-color: currentColor;
  -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M18 6L6 18M6 6l12 12'/%3E%3C/svg%3E");
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M18 6L6 18M6 6l12 12'/%3E%3C/svg%3E");
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}
.peblla-login-root .web_components_modal_close,
.peblla-login-root .ant-drawer-close,
.overall_situation_modal_tips .web_components_modal_close {
  color: var(--peblla-text-muted, rgba(0, 0, 0, 0.5)) !important;
  opacity: 0.85;
}
.peblla-login-root .web_components_modal_close:hover,
.peblla-login-root .ant-drawer-close:hover,
.overall_situation_modal_tips .web_components_modal_close:hover {
  color: var(--peblla-text, #1f1f1f) !important;
  opacity: 1;
}
.peblla-login-root .ant-drawer-close .anticon,
.peblla-login-root .ant-drawer-close svg { display: none !important; }
.peblla-login-root .ant-drawer-close::before {
  content: '' !important;
  display: inline-block;
  width: 22px;
  height: 22px;
  /* antd 5 css-in-js 让 ::before 拿不到父按钮的 currentColor，显式指定避免背景色塌成深色不可见 */
  color: var(--peblla-text-muted, rgba(0, 0, 0, 0.5)) !important;
  background-color: var(--peblla-text-muted, rgba(0, 0, 0, 0.5)) !important;
  -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M18 6L6 18M6 6l12 12'/%3E%3C/svg%3E");
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M18 6L6 18M6 6l12 12'/%3E%3C/svg%3E");
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  vertical-align: middle;
}

/* ============================================================
   4. Form fields
   ============================================================ */
.peblla-login-root .web_components_form_item {
  margin-bottom: var(--peblla-field-gap, 10px) !important;
}
.peblla-login-root .web_components_form_label {
  color: var(--peblla-text-muted, rgba(0, 0, 0, 0.5)) !important;
  font-size: var(--peblla-font-size-label, 12px) !important;
  font-weight: var(--peblla-font-weight-label, 400) !important;
  letter-spacing: var(--peblla-letter-spacing-label, normal) !important;
  text-transform: var(--peblla-text-transform-label, none) !important;
  font-family: var(--peblla-font-family, inherit) !important;
  line-height: 20px !important;
}
.peblla-login-root .web_components_form_label_required {
  color: var(--peblla-primary, #1677ff) !important;
}

/* ============================================================
   5. Inputs (data-peblla-part is on the <input> itself)
   下划线在 wrapper 上画，覆盖 +1 前缀和 Send code 后缀；同样适用于
   complete 弹窗里的 First/Last/Email input（data-peblla-part="complete-input"）
   ============================================================ */
.peblla-login-root .web_components_input:has([data-peblla-part="phone-input"]),
.peblla-login-root .web_components_input:has([data-peblla-part="code-input"]),
.peblla-login-root .web_components_input:has([data-peblla-part="complete-input"]) {
  border: none !important;
  border-bottom: 1px solid var(--peblla-input-border, rgba(0, 0, 0, 0.18)) !important;
  border-radius: var(--peblla-input-radius, 0) !important;
  transition: border-color 0.2s;
}
.peblla-login-root .web_components_input:has([data-peblla-part="phone-input"]:focus),
.peblla-login-root .web_components_input:has([data-peblla-part="code-input"]:focus),
.peblla-login-root .web_components_input:has([data-peblla-part="complete-input"]:focus) {
  border-bottom-color: var(--peblla-input-border-focus, var(--peblla-primary, #1677ff)) !important;
}

.peblla-login-root [data-peblla-part="phone-input"],
.peblla-login-root [data-peblla-part="code-input"],
.peblla-login-root [data-peblla-part="complete-input"] {
  background: transparent !important;
  color: var(--peblla-text, #1f1f1f) !important;
  caret-color: var(--peblla-primary, #1677ff) !important;
  border: none !important;
  font-size: var(--peblla-font-size-input, 14px) !important;
  font-weight: var(--peblla-font-weight-input, 400) !important;
  letter-spacing: var(--peblla-letter-spacing-input, normal) !important;
  font-family: var(--peblla-font-family, inherit) !important;
  height: var(--peblla-input-height, 32px) !important;
}
.peblla-login-root [data-peblla-part="phone-input"]:focus,
.peblla-login-root [data-peblla-part="code-input"]:focus,
.peblla-login-root [data-peblla-part="complete-input"]:focus {
  outline: none !important;
  box-shadow: none !important;
}
.peblla-login-root [data-peblla-part="phone-input"]::placeholder,
.peblla-login-root [data-peblla-part="code-input"]::placeholder,
.peblla-login-root [data-peblla-part="complete-input"]::placeholder {
  color: var(--peblla-text-muted, rgba(0, 0, 0, 0.4)) !important;
  opacity: 1;
}
.peblla-login-root [data-peblla-part="phone-prefix"] {
  color: var(--peblla-primary, #1677ff) !important;
  font-weight: 500;
  font-size: var(--peblla-font-size-input, 14px) !important;
}

/* Defensive: input.module.scss 锁死 color: rgba(0,0,0,.88)，要赢具体度 */
.peblla-login-root input.web_components_input,
.peblla-login-root input.web_components_form_widget {
  color: inherit !important;
  background: transparent !important;
  caret-color: currentColor !important;
}
.peblla-login-root .web_components_input .web_components_input {
  color: inherit !important;
  background: transparent !important;
  caret-color: currentColor !important;
}
.peblla-login-root .web_components_input .web_components_input::placeholder {
  color: var(--peblla-text-muted, rgba(0, 0, 0, 0.4)) !important;
  opacity: 1;
}

/* ============================================================
   6. Buttons (Send code / Continue / Sign Up / Oops OK)
   ============================================================ */
.peblla-login-root [data-peblla-part="submit-btn"] {
  background: var(--peblla-primary, #1677ff) !important;
  background-color: var(--peblla-primary, #1677ff) !important;
  border-color: var(--peblla-primary, #1677ff) !important;
  color: var(--peblla-on-primary, #fff) !important;
  border-radius: var(--peblla-button-radius, var(--peblla-radius, 8px)) !important;
  height: var(--peblla-button-height, 42px) !important;
  font-size: var(--peblla-font-size-button, 16px) !important;
  font-weight: var(--peblla-font-weight-button, 500) !important;
  letter-spacing: var(--peblla-letter-spacing-button, normal) !important;
  text-transform: var(--peblla-text-transform-button, none) !important;
  font-family: var(--peblla-font-family, inherit) !important;
  box-shadow: var(--peblla-button-shadow, none) !important;
  width: 100%;
  /* 跟项目 .login_btn 默认 margin-top: 4px 对齐，让 login Continue 和
     complete Sign Up 在两个抽屉里离表单底部留同样间距 */
  margin-top: 4px !important;
}
.peblla-login-root [data-peblla-part="submit-btn"]:hover {
  background: var(--peblla-primary-hover) !important;
  background-color: var(--peblla-primary-hover) !important;
  border-color: var(--peblla-primary-hover) !important;
}

.peblla-login-root [data-peblla-part="send-code-btn"] {
  background: transparent !important;
  background-color: transparent !important;
  color: var(--peblla-primary, #1677ff) !important;
  border: 1px solid var(--peblla-primary, #1677ff) !important;
  border-radius: var(--peblla-button-radius, var(--peblla-radius, 8px)) !important;
  font-family: var(--peblla-font-family, inherit) !important;
  /* 项目原 SCSS 锁死 height:26px，但 antd css-in-js 又塞 padding:12px 15px → 内容被压扁文字溢出框。覆盖。
     高度小于 wrapper (input-height) 几 px，让按钮在 wrapper 里垂直居中后上下各留 ~3px
     呼吸空间，按钮底沿与 input 下划线之间有视觉间距 */
  height: 26px !important;
  padding: 0 12px !important;
  line-height: 1 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  /* antd 5 primary button 默认带 0 2px 0 0 投影，颜色 ~#FFFDF0 在金色/深色主题里
     表现成按钮下方一条 2px 实线。清掉 */
  box-shadow: none !important;
}

/* antd <Button type="primary"> rendered outside our ConfigProvider
   (Oops OK, any future imperative modal raised by request.tsx) */
.web_components_modal_info_footer_button_ok:not(.ant-btn-background-ghost),
.web_components_modal_confirm_footer_button_ok:not(.ant-btn-background-ghost),
.overall_situation_modal_tips .ant-btn-primary:not(.ant-btn-background-ghost) {
  background-color: var(--peblla-primary, #1677ff) !important;
  border-color: var(--peblla-primary, #1677ff) !important;
  color: var(--peblla-on-primary, #fff) !important;
  border-radius: var(--peblla-button-radius, var(--peblla-radius, 8px)) !important;
  height: var(--peblla-button-height, 42px) !important;
  font-weight: var(--peblla-font-weight-button, 500) !important;
  letter-spacing: var(--peblla-letter-spacing-button, normal) !important;
  text-transform: var(--peblla-text-transform-button, none) !important;
}
.web_components_modal_info_footer_button_ok:not(.ant-btn-background-ghost):hover,
.web_components_modal_confirm_footer_button_ok:not(.ant-btn-background-ghost):hover,
.overall_situation_modal_tips .ant-btn-primary:not(.ant-btn-background-ghost):hover {
  background-color: var(--peblla-primary-hover) !important;
  border-color: var(--peblla-primary-hover) !important;
}

/* ============================================================
   7. Checkbox (complete 的 marketing 同意框)
   ============================================================ */
.peblla-login-root .ant-checkbox-checked .ant-checkbox-inner {
  background-color: var(--peblla-primary, #1677ff) !important;
  border-color: var(--peblla-primary, #1677ff) !important;
}
.peblla-login-root .ant-checkbox-wrapper {
  color: var(--peblla-text, #1f1f1f) !important;
  font-family: var(--peblla-font-family, inherit) !important;
  /* 项目 complete .form_allow_sms_checkbox 默认 14px，与 input 同档显式锁住 */
  font-size: 14px !important;
  line-height: 20px;
}

/* ============================================================
   8. Agreement / voice CTA / voice prompt
   ============================================================ */
.peblla-login-root [data-peblla-part="agreement"] {
  color: var(--peblla-agreement-color, var(--peblla-text-muted, rgba(0, 0, 0, 0.5))) !important;
  font-size: var(--peblla-font-size-agreement, 12px) !important;
  font-family: var(--peblla-font-family, inherit) !important;
  /* 跟项目 .login_agreement 自然默认 24px 对齐 */
  margin-top: 24px;
  line-height: 16px;
}
.peblla-login-root [data-peblla-part="agreement"] a,
.peblla-login-root [data-peblla-part="agreement"] span[onclick],
.peblla-login-root [data-peblla-part="agreement"] span:not([data-peblla-part="agreement"]) {
  color: var(--peblla-link-color, var(--peblla-accent, var(--peblla-primary, #1677ff))) !important;
  text-decoration: var(--peblla-link-decoration, underline) !important;
  cursor: pointer;
}
.peblla-login-root [data-peblla-part="agreement"] a:hover {
  text-decoration: var(--peblla-link-decoration-hover, underline) !important;
}
.peblla-login-root [data-peblla-part="voice-prompt"] {
  color: var(--peblla-text-muted, rgba(0, 0, 0, 0.5)) !important;
}
.peblla-login-root [data-peblla-part="voice-cta"] {
  color: var(--peblla-accent, var(--peblla-primary, #1677ff)) !important;
}

/* ============================================================
   9. Functional layout for the global Oops popup
   ============================================================ */
.overall_situation_modal_tips .web_components_modal_wrap,
.overall_situation_modal_tips .web_components_modal_mask_show {
  z-index: 1100 !important;
}
.overall_situation_modal_tips .web_components_modal_content .web_components_modal_body {
  font-size: 16px;
  padding: 12px 20px 4px 20px;
  text-align: center;
  line-height: 1.5;
}
.overall_situation_modal_tips .web_components_modal_content .web_components_modal_header {
  margin-bottom: 0;
  text-align: center;
}
.overall_situation_modal_tips .web_components_modal_footer {
  padding: 16px 20px 4px;
}
.overall_situation_modal_tips .web_components_modal_footer button {
  height: 42px;
  font-size: 16px;
}
`;

export const injectWidgetOverrides = (): void => {
  if (typeof document === "undefined") return;
  const id = "peblla-css-widget-overrides";
  if (document.getElementById(id)) return;
  const el = document.createElement("style");
  el.id = id;
  el.textContent = CSS;
  document.head.appendChild(el);
};
